import { useState, useEffect } from 'react'
import { Row, Col, Card, Button, Badge, Table, Form, InputGroup, Alert, Spinner, Dropdown, ButtonGroup } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { api } from '../../services/api'

interface Ticket {
  id: string
  subject: string
  description: string
  status: string
  priority: string
  category?: string
  createdAt: string
  updatedAt: string
  org?: {
    id: string
    name: string
    slug: string
  }
  requester: {
    name: string
    email: string
  }
  assignee?: {
    name: string
    email: string
  }
  tags: Array<{
    tag: {
      name: string
    }
  }>
  _count: {
    comments: number
    attachments: number
  }
}

interface FilterState {
  status: string
  priority: string
  category: string
  assignee: string
  tag: string
  dateFrom: string
  dateTo: string
  search: string
}

export const AgentTicketsPage = () => {
  const { user } = useAuth() // eslint-disable-line @typescript-eslint/no-unused-vars
  const navigate = useNavigate()
  const [tickets, setTickets] = useState<Ticket[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [activeQueue, setActiveQueue] = useState<'unassigned' | 'my' | 'all'>('unassigned')
  const [showFilters, setShowFilters] = useState(false)
  const [assignees, setAssignees] = useState<any[]>([])
  const [categories, setCategories] = useState<any[]>([])
  const [tags, setTags] = useState<any[]>([])
  const [filters, setFilters] = useState<FilterState>({
    status: '',
    priority: '',
    category: '',
    assignee: '',
    tag: '',
    dateFrom: '',
    dateTo: '',
    search: ''
  })

  useEffect(() => {
    fetchTickets()
    fetchFilterData()
  }, [activeQueue, filters])

  const fetchTickets = async () => {
    try {
      setLoading(true)
      const params = new URLSearchParams()
      
      // Add queue filter
      if (activeQueue === 'unassigned') {
        params.append('assigneeId', 'null')
      } else if (activeQueue === 'my') {
        params.append('assigneeId', user?.id || '')
      }
      
      // Add other filters
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value)
      })
      
      const response = await api.get(`/tickets?${params.toString()}`)
      setTickets(response.data.data || [])
    } catch (err: any) {
      setError('Failed to load tickets')
    } finally {
      setLoading(false)
    }
  }

  const fetchFilterData = async () => {
    try {
      // Fetch assignees, categories, and tags for filter dropdowns
      const [assigneesRes, categoriesRes, tagsRes] = await Promise.all([
        api.get('/users'),
        api.get('/admin/categories'),
        api.get('/admin/tags')
      ])
      
      setAssignees(assigneesRes.data.data || [])
      setCategories(categoriesRes.data.data || [])
      setTags(tagsRes.data.data || [])
    } catch (err) {
    }
  }

  const handleFilterChange = (key: keyof FilterState, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }))
  }

  const clearFilters = () => {
    setFilters({
      status: '',
      priority: '',
      category: '',
      assignee: '',
      tag: '',
      dateFrom: '',
      dateTo: '',
      search: ''
    })
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'Urgent': return 'danger'
      case 'High': return 'warning'
      case 'Medium': return 'info'
      case 'Low': return 'secondary'
      default: return 'secondary'
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'OPEN': return 'primary'
      case 'IN_PROGRESS': return 'warning'
      case 'RESOLVED': return 'success'
      case 'CLOSED': return 'secondary'
      default: return 'secondary'
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getQueueStats = () => {
    const unassigned = tickets.filter(t => !t.assignee).length
    const myTickets = tickets.filter(t => t.assignee?.email === user?.email).length
    const total = tickets.length
    
    return { unassigned, myTickets, total }
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    )
  }

  const stats = getQueueStats()

  return (
    <div className="agent-tickets-container">
      {/* Header */}
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-3">
        <div className="mb-2 mb-sm-0">
          <h2 className="mb-1">Agent Tickets</h2>
          <p className="text-muted mb-0 small">Manage and process support tickets</p>
        </div>
        <div className="d-flex gap-2">
          <Button 
            variant="outline-primary" 
            onClick={() => setShowFilters(!showFilters)}
            className="d-flex align-items-center"
          >
            <i className="fas fa-filter me-2"></i>
            {showFilters ? 'Hide' : 'Show'} Filters
          </Button>
          <Button variant="primary" onClick={() => navigate('/tickets/new')} className="d-flex align-items-center">
            <i className="fas fa-plus me-2"></i>
            New Ticket
          </Button>
        </div>
      </div>

      {/* Queue Tabs */}
      <Card className="mb-4 border-0 shadow-sm">
        <Card.Body className="p-0">
          <ButtonGroup className="w-100">
            <Button
              variant={activeQueue === 'unassigned' ? 'primary' : 'outline-primary'}
              onClick={() => setActiveQueue('unassigned')}
              className="d-flex justify-content-between align-items-center"
            >
              <span>
                <i className="fas fa-exclamation-triangle me-2"></i>
                Unassigned
              </span>
              <Badge bg="secondary" className="ms-2">{stats.unassigned}</Badge>
            </Button>
            <Button
              variant={activeQueue === 'my' ? 'primary' : 'outline-primary'}
              onClick={() => setActiveQueue('my')}
              className="d-flex justify-content-between align-items-center"
            >
              <span>
                <i className="fas fa-user me-2"></i>
                My Tickets
              </span>
              <Badge bg="secondary" className="ms-2">{stats.myTickets}</Badge>
            </Button>
            <Button
              variant={activeQueue === 'all' ? 'primary' : 'outline-primary'}
              onClick={() => setActiveQueue('all')}
              className="d-flex justify-content-between align-items-center"
            >
              <span>
                <i className="fas fa-list me-2"></i>
                All Tickets
              </span>
              <Badge bg="secondary" className="ms-2">{stats.total}</Badge>
            </Button>
          </ButtonGroup>
        </Card.Body>
      </Card>

      {/* Advanced Filters */}
      {showFilters && (
        <Card className="mb-4 border-0 shadow-sm">
          <Card.Header className="bg-white border-bottom">
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                <i className="fas fa-filter me-2 text-primary"></i>
                Advanced Filters
              </h5>
              <Button variant="outline-secondary" size="sm" onClick={clearFilters}>
                <i className="fas fa-times me-2"></i>Clear All
              </Button>
            </div>
          </Card.Header>
          <Card.Body>
            <Row className="g-3">
              <Col md={3}>
                <Form.Label>Search</Form.Label>
                <InputGroup>
                  <InputGroup.Text>
                    <i className="fas fa-search"></i>
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Search tickets..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                  />
                </InputGroup>
              </Col>
              
              <Col md={2}>
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <option value="">All Status</option>
                  <option value="OPEN">Open</option>
                  <option value="IN_PROGRESS">In Progress</option>
                  <option value="RESOLVED">Resolved</option>
                  <option value="CLOSED">Closed</option>
                </Form.Select>
              </Col>
              
              <Col md={2}>
                <Form.Label>Priority</Form.Label>
                <Form.Select
                  value={filters.priority}
                  onChange={(e) => handleFilterChange('priority', e.target.value)}
                >
                  <option value="">All Priority</option>
                  <option value="Urgent">Urgent</option>
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                  <option value="Low">Low</option>
                </Form.Select>
              </Col>
              
              <Col md={2}>
                <Form.Label>Category</Form.Label>
                <Form.Select
                  value={filters.category}
                  onChange={(e) => handleFilterChange('category', e.target.value)}
                >
                  <option value="">All Categories</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.name}>{cat.name}</option>
                  ))}
                </Form.Select>
              </Col>
              
              <Col md={3}>
                <Form.Label>Assignee</Form.Label>
                <Form.Select
                  value={filters.assignee}
                  onChange={(e) => handleFilterChange('assignee', e.target.value)}
                >
                  <option value="">All Assignees</option>
                  <option value="unassigned">Unassigned</option>
                  {assignees.map((assignee) => (
                    <option key={assignee.id} value={assignee.id}>{assignee.name}</option>
                  ))}
                </Form.Select>
              </Col>
            </Row>
            
            <Row className="g-3 mt-2">
              <Col md={3}>
                <Form.Label>Tag</Form.Label>
                <Form.Select
                  value={filters.tag}
                  onChange={(e) => handleFilterChange('tag', e.target.value)}
                >
                  <option value="">All Tags</option>
                  {tags.map((tag) => (
                    <option key={tag.id} value={tag.name}>{tag.name}</option>
                  ))}
                </Form.Select>
              </Col>
              
              <Col md={3}>
                <Form.Label>Date From</Form.Label>
                <Form.Control
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                />
              </Col>
              
              <Col md={3}>
                <Form.Label>Date To</Form.Label>
                <Form.Control
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                />
              </Col>
              
              <Col md={3} className="d-flex align-items-end">
                <Button 
                  variant="outline-primary" 
                  onClick={fetchTickets}
                  className="w-100"
                >
                  <i className="fas fa-search me-2"></i>Apply Filters
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* Tickets Table */}
      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-white border-bottom">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <i className="fas fa-ticket-alt me-2 text-primary"></i>
              {activeQueue === 'unassigned' ? 'Unassigned' : 
               activeQueue === 'my' ? 'My' : 'All'} Tickets ({tickets.length})
            </h5>
            <Button 
              variant="outline-primary" 
              size="sm"
              onClick={fetchTickets}
            >
              <i className="fas fa-sync-alt me-2"></i>Refresh
            </Button>
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          {error && (
            <Alert variant="danger" className="m-3">
              {error}
            </Alert>
          )}
          
          {tickets.length > 0 ? (
            <Table hover className="mb-0">
              <thead className="bg-light">
                <tr>
                  <th>Subject</th>
                  {user?.role === 'ADMIN' && <th>Organization</th>}
                  <th>Status</th>
                  <th>Priority</th>
                  <th>Assignee</th>
                  <th>Requester</th>
                  <th>Created</th>
                  <th>Activity</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tickets.map((ticket) => (
                  <tr key={ticket.id}>
                    <td>
                      <div>
                        <div className="fw-medium">{ticket.subject}</div>
                        <small className="text-muted">
                          {ticket.description.substring(0, 100)}
                          {ticket.description.length > 100 && '...'}
                        </small>
                        {ticket.tags.length > 0 && (
                          <div className="mt-1">
                            {ticket.tags.slice(0, 3).map((tag, index) => (
                              <Badge key={index} bg="light" text="dark" className="me-1">
                                {tag.tag.name}
                              </Badge>
                            ))}
                            {ticket.tags.length > 3 && (
                              <Badge bg="secondary">+{ticket.tags.length - 3}</Badge>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    {user?.role === 'ADMIN' && (
                      <td>
                        <Badge bg="info" className="text-dark">
                          {ticket.org?.name || 'Unknown'}
                        </Badge>
                      </td>
                    )}
                    <td>
                      <Badge bg={getStatusColor(ticket.status)}>
                        {ticket.status}
                      </Badge>
                    </td>
                    <td>
                      <Badge bg={getPriorityColor(ticket.priority)}>
                        {ticket.priority}
                      </Badge>
                    </td>
                    <td>
                      {ticket.assignee ? (
                        <div>
                          <div className="fw-medium">{ticket.assignee.name}</div>
                          <small className="text-muted">{ticket.assignee.email}</small>
                        </div>
                      ) : (
                        <Badge bg="warning">Unassigned</Badge>
                      )}
                    </td>
                    <td>
                      <div>
                        <div className="fw-medium">{ticket.requester.name}</div>
                        <small className="text-muted">{ticket.requester.email}</small>
                      </div>
                    </td>
                    <td>
                      <div>
                        <div>{formatDate(ticket.createdAt)}</div>
                        <small className="text-muted">
                          {Math.floor((Date.now() - new Date(ticket.createdAt).getTime()) / (1000 * 60 * 60 * 24))} days ago
                        </small>
                      </div>
                    </td>
                    <td>
                      <div className="d-flex gap-2">
                        <small className="text-muted">
                          <i className="fas fa-comments me-1"></i>
                          {ticket._count.comments}
                        </small>
                        <small className="text-muted">
                          <i className="fas fa-paperclip me-1"></i>
                          {ticket._count.attachments}
                        </small>
                      </div>
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        <Button 
                          variant="outline-primary" 
                          size="sm"
                          onClick={() => navigate(`/tickets/${ticket.id}`)}
                        >
                          <i className="fas fa-eye"></i>
                        </Button>
                        <Dropdown>
                          <Dropdown.Toggle variant="outline-secondary" size="sm">
                            <i className="fas fa-ellipsis-v"></i>
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            <Dropdown.Item onClick={() => navigate(`/tickets/${ticket.id}`)}>
                              <i className="fas fa-eye me-2"></i>View Details
                            </Dropdown.Item>
                            <Dropdown.Item onClick={() => navigate(`/tickets/${ticket.id}#comments`)}>
                              <i className="fas fa-comments me-2"></i>View Comments
                            </Dropdown.Item>
                            <Dropdown.Divider />
                            <Dropdown.Item>
                              <i className="fas fa-user-plus me-2"></i>Assign to Me
                            </Dropdown.Item>
                            <Dropdown.Item>
                              <i className="fas fa-edit me-2"></i>Change Status
                            </Dropdown.Item>
                            <Dropdown.Divider />
                            <Dropdown.Item className="text-danger">
                              <i className="fas fa-archive me-2"></i>Archive
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="text-center py-5">
              <i className="fas fa-inbox text-muted" style={{ fontSize: '3rem' }}></i>
              <h5 className="mt-3 text-muted">No tickets found</h5>
              <p className="text-muted">No tickets match your current filters</p>
              <Button variant="outline-primary" onClick={clearFilters}>
                <i className="fas fa-times me-2"></i>Clear Filters
              </Button>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  )
}

